import type { FC, MouseEvent } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { alpha, Box, Button, IconButton, Paper, Portal, Typography } from '@mui/material'
import { Close, Login } from '@mui/icons-material'
import { useAuthActions } from '../hooks'

interface LoginModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onMinimize?: () => void
}

export const LoginModal: FC<LoginModalProps> = ({ open, onClose, onMinimize }) => {
  const { login, isLoggingIn, loginError } = useAuthActions()

  const handleBackdropClick = useCallback(
    (event: MouseEvent): void => {
      if (event.target === event.currentTarget) {
        onClose()
      }
    },
    [onClose],
  )

  const handleLogin = useCallback((): void => {
    login().catch(() => {})
  }, [login])

  if (!open) return null

  return (
    <Portal>
      <Box sx={overlaySx} onClick={handleBackdropClick}>
        <Paper elevation={8} sx={modalPaperSx}>
          {onMinimize && (
            <IconButton
              onClick={onMinimize}
              sx={closeButtonSx}
              size="small"
              aria-label="Minimize login modal"
            >
              <Close />
            </IconButton>
          )}

          <Typography variant="h5" sx={titleSx}>
            Welcome
          </Typography>

          {loginError && (
            <Typography variant="body2" sx={errorSx}>
              {loginError}
            </Typography>
          )}

          <Button
            variant="contained"
            size="medium"
            onClick={handleLogin}
            startIcon={<Login />}
            disabled={isLoggingIn}
            sx={loginButtonSx}
          >
            {isLoggingIn ? 'Signing In...' : 'Sign In'}
          </Button>
        </Paper>
      </Box>
    </Portal>
  )
}

const overlaySx: SxProps<Theme> = (theme) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(theme.palette.mode === 'dark' ? '#000000' : '#3E2723', 0.5),
  zIndex: 1200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 2,
  animation: 'fadeIn 0.3s ease-out',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
})

const modalPaperSx: SxProps<Theme> = {
  p: 5,
  borderRadius: 2,
  minWidth: 360,
  maxWidth: 400,
  textAlign: 'center',
  position: 'relative',
  backgroundColor: 'background.paper',
}

const closeButtonSx: SxProps<Theme> = {
  position: 'absolute',
  top: 12,
  right: 12,
  color: 'text.secondary',
  '&:hover': {
    color: 'error.main',
    backgroundColor: 'action.hover',
  },
}

const titleSx: SxProps<Theme> = {
  mb: 4,
}

const errorSx: SxProps<Theme> = (theme) => ({
  mb: 2,
  p: 1,
  color: 'error.main',
  bgcolor: alpha(theme.palette.error.main, 0.1),
  borderRadius: 1,
})

const loginButtonSx: SxProps<Theme> = {
  px: 3,
  py: 1,
  fontSize: '0.95rem',
  fontWeight: 500,
  borderRadius: 2,
  textTransform: 'none',
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  boxShadow: 1,
  '&:hover': {
    backgroundColor: 'primary.dark',
    boxShadow: 2,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: 'action.disabledBackground',
    color: 'action.disabled',
    boxShadow: 'none',
  },
  transition: 'all 0.2s ease-in-out',
}
